import { observer } from 'mobx-react-lite'
import { type FC, type MouseEventHandler, type ReactNode, useEffect, useState } from 'react'
import type WaveSurfer from 'wavesurfer.js'
import { Icon, Typography } from 'shared/ui'
import { getAriaLabel, logger } from 'shared/lib'
import { PlayBtn } from 'shared/ui/Button/ui/PlayBtn/PlayBtn'
import { uiStore } from 'shared/store/uiStore'
import { AudioWave } from 'shared/ui/AudioPlayer'
import { type IResponseVoice } from 'entities/TextToSpeech'
import { useTextToSpeechContext } from 'widgets/TextToSpeech/context/context'
import styles from './styles.module.scss'

export type IVoiceItemProps = {
  voice: IResponseVoice
  onCloseMenu: () => void
  getVoicePreview: (id: string) => Promise<string | undefined>
  rightContent?: ReactNode
  onErrorLoadPreview: (id: string) => void
  isLoading?: boolean
}

export const VoiceItem: FC<IVoiceItemProps> = observer(
  ({ voice, onCloseMenu, getVoicePreview, rightContent, onErrorLoadPreview, isLoading }) => {
    const { selectedVoice, setSelectedVoice } = useTextToSpeechContext()

    const [audioSrc, setAudioSrc] = useState('')

    const [waveform, setWaveform] = useState<WaveSurfer | null>(null)
    const [playing, setPlaying] = useState(false)
    const [isLoadingAudio, setIsLoadingAudio] = useState(false)

    const play = async () => {
      if (!waveform) return

      try {
        await waveform.play()
      } catch (error) {
        logger.error(error)
      }
    }

    const pause = () => {
      if (!waveform) return

      try {
        waveform.pause()
      } catch (error) {
        logger.error(error)
      }
    }

    useEffect(() => {
      if (audioSrc && playing && waveform) {
        uiStore.setPlayAudioId(audioSrc)
        play()
      }
    }, [audioSrc, playing, waveform])

    const loadAudio = async () => {
      setIsLoadingAudio(true)

      try {
        const src = await getVoicePreview(voice.id)

        if (src) {
          setAudioSrc(src)
          return src
        }
      } catch (e) {
        logger.error(e)
      } finally {
        setIsLoadingAudio(false)
      }
    }

    const togglePlay: MouseEventHandler<HTMLButtonElement> = async (e) => {
      e.stopPropagation()
      try {
        if (playing) {
          pause()
        } else {
          if (!audioSrc) {
            const src = await loadAudio()
            if (src) {
              setPlaying(true)
            }
          } else {
            setPlaying(true)
          }
        }
      } catch (e) {
        logger.error(e)
      }
    }

    const handleSelectVoice = () => {
      onCloseMenu()
      setSelectedVoice(voice)
    }

    const onErrorLoad = () => {
      onErrorLoadPreview(voice.id)
      setAudioSrc('')
      setPlaying(false)
    }

    return (
      <button className={styles.wrap} onClick={handleSelectVoice}>
        <div className={styles.wrapPlay}>
          <PlayBtn
            isPlay={playing}
            togglePlay={togglePlay}
            color={'primary'}
            transparentBackground
            size={'small'}
            withoutTooltip
            disabled={!!isLoading || isLoadingAudio}
          />
        </div>

        <div className={styles.wrapName}>
          <Typography ariaLabel={getAriaLabel(voice.name)} variant={'text-input-field-r'}>
            {voice.name}
          </Typography>
          {audioSrc && (
            <div onClick={(e) => e.stopPropagation()}>
              <AudioWave
                autoplay
                url={audioSrc}
                waveform={waveform}
                setWaveform={setWaveform}
                id={audioSrc}
                playing={playing}
                setPlaying={setPlaying}
                width={50}
                hide={!playing}
                onErrorLoad={onErrorLoad}
              />
            </div>
          )}
        </div>
        {rightContent ||
          (selectedVoice?.id === voice.id && <Icon icon={'check1'} className={styles.check} />)}
      </button>
    )
  }
)
